import { Slide } from '@llx/common';
import { CaseMap } from '@llx/common';
import { useEffect, useMemo, useState } from 'react';

import {
  SlideStatsFieldsFragment,
  SlideStatsWhereInput,
  useListSlideStatsQuery,
} from '@/@generated/codegen/API';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { parseSlideStatsPerCase } from '../utils/parseSlideStatsPerCase';

export default function useMeetingSlideStats(
  activated: boolean,
  areRequirementsLoading: boolean,
  meetingID: string | undefined,
  caseMap: CaseMap,
  caseIds: string[]
) {
  const [slideStatsPerCase, setSlideStatsPerCase] = useState<
    Record<string, SlideStatsFieldsFragment[]>
  >({});
  const where: SlideStatsWhereInput = useMemo(() => {
    return {
      meetingId: {
        equals: meetingID,
      },
    };
  }, [meetingID]);
  const slideStatsQuery = useListSlideStatsQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where,
    },
  });

  useEffect(() => {
    if (!slideStatsQuery.data) return;
    if (!activated || areRequirementsLoading) return;
    const newSlideStatsPerCase: Record<string, SlideStatsFieldsFragment[]> =
      slideStatsQuery.data.listSlideStats.edges.reduce(
        (acc, curr) => {
          const slideStats = curr.node;
          if (!slideStats || !slideStats.patientCaseId) return acc;
          if (!acc[slideStats.patientCaseId]) {
            acc[slideStats.patientCaseId] = [];
          }
          acc[slideStats.patientCaseId].push(slideStats);
          return acc;
        },
        {} as Record<string, SlideStatsFieldsFragment[]>
      );
    // NOTE: remove stats from deleted slides
    for (const caseId in newSlideStatsPerCase) {
      if (!caseMap[caseId]) {
        captureSentry({
          title:
            'useMeetingSlideStats | meeting has a slideStats object of a case not part of the caseCluster',
          detail: {
            caseId,
            caseMap,
            caseStats: slideStatsQuery.data?.listSlideStats.edges,
          },
        });
        continue;
      }
      newSlideStatsPerCase[caseId] = newSlideStatsPerCase[caseId].filter((slideStats) => {
        if (!slideStats || !slideStats.slideId) return false;
        const slideInfo = caseMap[caseId].slides?.find((slide) => slide.id === slideStats.slideId);
        if (!slideInfo) {
          captureSentry({
            title: 'useMeetingSlideStats | Found slide stats for a deleted slide (not an error)',
            detail: {
              slideId: slideStats.slideId,
              caseId,
              caseMap,
              slideStats,
            },
          });
          return false;
        }
        return true;
      });
    }
    setSlideStatsPerCase(newSlideStatsPerCase);
  }, [slideStatsQuery.data, activated, areRequirementsLoading, caseMap]);

  const parsedSlideStats = useMemo(() => {
    // NOTE: we don't want to continue if anything needed is still loading
    if (
      !activated ||
      areRequirementsLoading
      // NOTE: we can not check for this because this means that the host has to present all cases before being
      // able to see any slide stats which is not a good experience
      // Object.keys(slideStatsPerCase).length !== caseIds.length
    ) {
      return undefined;
    }
    const caseIdToSlideList: Record<string, Slide[]> = {};
    for (const caseId of caseIds) {
      caseIdToSlideList[caseId] = caseMap[caseId].slides || [];
    }
    return parseSlideStatsPerCase(caseIds, caseIdToSlideList, slideStatsPerCase);
  }, [caseIds, slideStatsPerCase, areRequirementsLoading, activated, caseMap]);

  useEntitySubscription('SlideStats', {
    onCreate: {
      variables: {
        where,
      },
      onData: (res) => {
        const obj = res.data.data?.onCreateSlideStats;
        if (!obj) {
          captureSentry({
            title: 'UseMeetingSlideStats | onData | onCreate | obj is undefined',
            detail: {},
          });
          return;
        }

        setSlideStatsPerCase((prev) => {
          const newObj: Record<string, SlideStatsFieldsFragment[]> = {
            ...prev,
          };
          if (!newObj[obj.patientCaseId]) {
            newObj[obj.patientCaseId] = [];
          }
          newObj[obj.patientCaseId].push(obj);
          return newObj;
        });
      },
    },
    onUpdate: {
      variables: {
        where,
      },
      onData: (res) => {
        const obj = res.data.data?.onUpdateSlideStats;
        if (!obj) {
          captureSentry({
            title: 'UseMeetingSlideStats | onData | onUpdate | obj is undefined',
            detail: {},
          });
          return;
        }

        setSlideStatsPerCase((prev) => {
          const newObj: Record<string, SlideStatsFieldsFragment[]> = {
            ...prev,
          };
          if (!newObj[obj.patientCaseId]) {
            newObj[obj.patientCaseId] = [];
          }
          const indexToUpdate = newObj[obj.patientCaseId].findIndex((item) => item.id === obj.id);
          if (indexToUpdate >= 0) {
            newObj[obj.patientCaseId][indexToUpdate] = obj;
          } else {
            captureSentry({
              title:
                'useMeetingSlideStats: onUpdateSlideStats: indexToUpdate null. This means that the updated slide was not in the list of slides which is not normal but not dangerous.',
              detail: {
                slideId: obj.slideId,
                id: obj.id,
              },
            });
          }
          return newObj;
        });
      },
    },
  });

  return {
    parsedSlideStats,
  };
}
